import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Reveal from "@/components/Reveal";
import SmartVideo from "@/components/SmartVideo";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useSiteContent } from "@/lib/content";

const HERO_IMG = "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=2400&auto=format&fit=crop";
const AI_IMG = "https://images.unsplash.com/photo-1434056886845-dac89ffe9b56?q=80&w=1800&auto=format&fit=crop";
const REEL_VIDEO = "https://videos.pexels.com/video-files/3141210/3141210-uhd_2560_1440_25fps.mp4";
const REEL_POSTER = "https://images.unsplash.com/photo-1478720568477-152d9b164e26?q=80&w=1600&auto=format&fit=crop";

const REEL_FRAMES = [
  "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=400&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=400&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1547996160-81dfa63595aa?q=80&w=400&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=400&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1470337458703-46ad1756a187?q=80&w=400&auto=format&fit=crop",
];

const AREAS = [
  {
    title: "PRODUCT",
    titleKey: "home_area1_title",
    testId: "area-card-product",
    path: "/product",
    img: "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "ARCHITECTURE",
    titleKey: "home_area2_title",
    testId: "area-card-architecture",
    path: "/architecture",
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "FOOD",
    titleKey: "home_area_food_title",
    testId: "area-card-food",
    path: "/food",
    img: "/media/nv/food-01.jpg",
  },
  {
    title: "MOTION / AI / VIDEO",
    titleKey: "home_area3_title",
    testId: "area-card-motion-ai",
    path: "/motion-ai",
    img: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?q=80&w=1200&auto=format&fit=crop",
  },
];

const CLIENTS = ["NARDO", "DIMAS", "rochebobois", "FIAM", "LEMA", "CDG", "MOBILFRESNO", "axolight"];

const useTexts = () => {
  const content = useSiteContent();
  return (key, fallback) => content?.texts?.[key] || fallback;
};

const ShowreelDialog = ({ open, onOpenChange, video, poster }) => {
  const tx = useTexts();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="showreel-modal" className="max-w-5xl w-[92vw] bg-night border-line p-0 overflow-hidden">
        {open && <SmartVideo testId="showreel-video" url={video || REEL_VIDEO} poster={poster || REEL_POSTER} />}
        <div className="px-6 py-4 flex items-center justify-between border-t border-line">
          <p className="text-xs font-mono tracking-[0.25em] text-fog">{tx("home_reel_title", "PERCEZIONE — SHOWREEL 2026")}</p>
          <p className="text-xs font-mono tracking-[0.25em] text-gold">{tx("home_reel_duration", "00:30")}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Hero = ({ onPlayReel, image }) => {
  const tx = useTexts();
  return (
    <section data-testid="hero-section" className="relative h-screen min-h-[640px] flex items-end lg:items-center">
      <img
        src={image || HERO_IMG}
        alt="Fotografia still life di gioielli — Nicola Verardo"
        className="absolute inset-0 w-full h-full object-cover"
        fetchpriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-night via-night/70 to-night/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-night via-transparent to-night/60" />
      <div className="relative z-10 px-6 sm:px-12 lg:px-20 pb-32 lg:pb-0 w-full">
        <Seo
          title="Nicola Verardo — Visual Production | Foto, Motion e AI"
          description="Boutique di visual production: fotografia still life, architettura, motion e AI coordinate sotto un'unica direzione. Immagini che costruiscono valore percepito."
          path="/"
        />
        <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.05] text-bone max-w-3xl">
          {tx("home_hero_title", "Costruisco immagini che aumentano il valore percepito di prodotti e spazi.")}
        </h1>
        <p data-testid="hero-subline" className="mt-8 text-xs sm:text-sm font-mono tracking-[0.3em] text-fog">
          {tx("home_hero_sub", "STILL LIFE • ARCHITECTURE • AI & MOTION")}
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/work"
            data-testid="hero-cta-work"
            className="inline-flex items-center gap-3 px-7 py-3.5 border border-bone/40 text-bone text-xs font-medium tracking-[0.2em] hover:border-gold hover:text-gold transition-[color,border-color] duration-300"
          >
            {tx("home_hero_cta_work", "VEDI I LAVORI")} <ArrowRight size={14} />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-3 px-7 py-3.5 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone transition-colors duration-300"
          >
            {tx("home_hero_cta_contact", "CONTATTAMI")}
          </Link>
        </div>
      </div>
      <button
        onClick={onPlayReel}
        data-testid="showreel-play-button"
        className="absolute bottom-10 right-6 sm:right-12 lg:right-20 z-10 hidden sm:flex items-center gap-4 text-xs font-mono tracking-[0.25em] text-bone hover:text-gold transition-colors duration-300"
      >
        {tx("home_hero_play", "PLAY SHOWREEL")}
        <span className="w-12 h-12 rounded-full border border-bone/50 flex items-center justify-center">
          <Play size={14} className="ml-0.5" />
        </span>
      </button>
    </section>
  );
};

const Positioning = ({ onPlayReel, frames }) => {
  const tx = useTexts();
  return (
    <section data-testid="positioning-section" className="bg-bone text-night">
      <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-32 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-night/50">{tx("home_pos_label", "Visual Production")}</p>
          <h2 className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight leading-snug">
            {tx("home_pos_title", "Immagini pensate per comunicare valore.")}
          </h2>
          <p className="mt-6 text-base text-night/60 leading-relaxed max-w-md">
            {tx("home_pos_text", "Unisco fotografia, video e AI per creare contenuti visivi su misura, capaci di raccontare l'essenza di un brand e trasformarla in percezione e desiderio.")}
          </p>
        </Reveal>
        <Reveal delay={150}>
          <button
            onClick={onPlayReel}
            data-testid="showreel-strip"
            className="relative block w-full group text-left"
            aria-label="Riproduci showreel"
          >
            <div className="grid grid-cols-5 gap-1 bg-night p-1">
              {(frames || REEL_FRAMES).map((frame, i) => (
                <img
                  key={i}
                  src={frame}
                  alt=""
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                />
              ))}
            </div>
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-bone flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Play size={20} className="ml-1 text-night" />
              </span>
            </span>
            <span className="absolute bottom-4 left-5 text-[10px] font-mono tracking-[0.25em] text-bone">{tx("home_reel_duration", "00:30")}</span>
          </button>
          <p className="mt-3 text-[10px] font-mono tracking-[0.25em] text-night/50">{tx("home_pos_caption", "SHOWREEL 2026")}</p>
        </Reveal>
      </div>
    </section>
  );
};

const Stats = () => {
  const tx = useTexts();
  const stats = [
    { value: tx("home_stat1_v", "26+"), label: tx("home_stat1_l", "ANNI DI ESPERIENZA") },
    { value: tx("home_stat2_v", "300+"), label: tx("home_stat2_l", "PROGETTI REALIZZATI") },
    { value: tx("home_stat3_v", "ITALIA / ESTERO"), label: tx("home_stat3_l", "CLIENTI E PROGETTI") },
    { value: tx("home_stat4_v", "3"), label: tx("home_stat4_l", "AREE DI SPECIALIZZAZIONE") },
  ];
  return (
    <section data-testid="stats-band" className="bg-night border-y border-line">
      <div className="grid grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <Reveal key={i} delay={i * 100} className={`px-6 sm:px-10 py-12 lg:py-16 ${i > 0 ? "border-l border-line" : ""} ${i > 1 ? "border-t lg:border-t-0 border-line" : ""}`}>
            <p data-testid={`stat-value-${i}`} className={`${stat.value.length > 6 ? "text-2xl lg:text-3xl" : "text-4xl lg:text-5xl"} font-light text-gold font-mono tracking-tight whitespace-nowrap`}>{stat.value}</p>
            <p className="mt-3 text-[10px] sm:text-xs font-mono tracking-[0.25em] text-fog">{stat.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Areas = ({ images }) => {
  const tx = useTexts();
  return (
    <section data-testid="areas-section" className="bg-bone text-night">
      <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-32">
        <Reveal>
          <p className="text-center text-xs font-mono uppercase tracking-[0.25em] text-night/50">{tx("home_areas_label", "Aree di lavoro")}</p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {AREAS.map((area, i) => (
            <Reveal key={area.titleKey} delay={i * 120}>
              <Link to={area.path} data-testid={area.testId} className="group block border border-night/10">
                <div className="overflow-hidden">
                  <img
                    src={(images && images[i]) || area.img}
                    alt={area.title}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
                <div className="flex items-center justify-between px-5 py-4">
                  <span className="text-xs font-medium tracking-[0.2em]">{tx(area.titleKey, area.title)}</span>
                  <ArrowRight size={16} className="text-night/40 group-hover:text-gold group-hover:translate-x-1 transition-[color,transform] duration-300" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const AISection = ({ image }) => {
  const tx = useTexts();
  return (
    <section data-testid="ai-section" className="bg-night">
      <div className="grid lg:grid-cols-2">
        <div className="relative min-h-[320px] lg:min-h-[560px]">
          <img src={image || AI_IMG} alt="Produzione visiva evoluta" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-night/40" />
        </div>
        <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-32 flex flex-col justify-center">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-fog">{tx("home_ai_label", "AI & Visual Production")}</p>
            <h2 className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight leading-snug text-bone">
              {tx("home_ai_title", "Produzione visiva evoluta.")}
            </h2>
            <p className="mt-6 text-base text-fog leading-relaxed max-w-md">
              {tx("home_ai_text", "Integro tecnologie AI e tecniche di compositing avanzato per creare ambientazioni, estensioni e contenuti impossibili da ottenere in modo tradizionale. Il prodotto resta fotograficamente reale: l'AI estende la fotografia, non la sostituisce.")}
            </p>
            <Link
              to="/motion-ai"
              data-testid="ai-section-cta"
              className="mt-10 inline-flex items-center gap-3 px-7 py-3.5 border border-bone/40 text-bone text-xs font-medium tracking-[0.2em] hover:border-gold hover:text-gold transition-[color,border-color] duration-300 w-fit"
            >
              {tx("home_ai_cta", "SCOPRI DI PIÙ")} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const SelectedWork = () => {
  const tx = useTexts();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((res) => {
      const featured = res.data.filter((p) => p.featured);
      setProjects(featured.length ? featured : res.data.slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <section data-testid="selected-work-section" className="bg-bone text-night">
      <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-32">
        <Reveal className="flex items-end justify-between gap-6">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-night/50">{tx("home_work_label", "Selected Work")}</p>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight">{tx("home_work_title", "Lavori selezionati.")}</h2>
          </div>
          <Link
            to="/work"
            data-testid="selected-work-all-link"
            className="hidden sm:inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-night/60 hover:text-night transition-colors duration-300"
          >
            {tx("home_work_all", "TUTTI I LAVORI")} <ArrowRight size={14} />
          </Link>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <Reveal key={project.slug} delay={(i % 2) * 120} className={(i === 0 || (i === projects.length - 1 && i % 2 === 1)) ? "md:col-span-2" : ""}>
              <Link to={`/work/${project.slug}`} data-testid="case-study-card" className="group block">
                <div className="overflow-hidden bg-night/5">
                  <img
                    src={project.hero}
                    alt={project.title}
                    loading="lazy"
                    className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out ${(i === 0 || (i === projects.length - 1 && i % 2 === 1)) ? "aspect-[21/9]" : "aspect-[16/9]"}`}
                  />
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium tracking-tight">{project.title}</h3>
                    <p className="mt-1 text-xs font-mono tracking-[0.2em] text-night/50 uppercase">{project.client} — {project.year}</p>
                  </div>
                  <ArrowRight size={16} className="shrink-0 text-night/40 group-hover:text-gold group-hover:translate-x-1 transition-[color,transform] duration-300" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const tx = useTexts();
  return (
    <section data-testid="manifesto-section" className="bg-night">
      <div className="px-6 sm:px-12 lg:px-20 py-24 lg:py-36 max-w-4xl">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-fog">{tx("home_manifesto_label", "Approccio")}</p>
          <blockquote className="mt-8 text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-snug text-bone">
            {tx("home_manifesto_quote", "«Non produco immagini isolate. Progetto sistemi visivi che aumentano il valore percepito del brand nel tempo.»")}
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
};

const Clients = () => {
  const content = useSiteContent();
  const tx = (key, fallback) => content?.texts?.[key] || fallback;
  const testimonials = content?.testimonials?.length
    ? content.testimonials
    : [1, 2, 3]
        .map((n) => ({ quote: tx(`home_test${n}_q`, ""), author: tx(`home_test${n}_a`, "") }))
        .filter((t) => t.quote || t.author);
  const rawClients = content?.clients?.length
    ? content.clients
    : [1, 2, 3, 4, 5, 6, 7, 8].map((n) => tx(`home_client_${n}`, CLIENTS[n - 1] || "")).filter(Boolean);
  const clientList = rawClients.map((c) => (typeof c === "string" ? { name: c, logo: "" } : c));
  return (
    <section data-testid="clients-section" className="bg-bone text-night">
      <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-28">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-night/50">{tx("home_clients_label", "Alcuni clienti")}</p>
          <div data-testid="client-logos" className="mt-10 flex flex-wrap items-center gap-x-12 gap-y-6">
            {clientList.map((client, i) =>
              client.logo ? (
                <img key={i} src={client.logo} alt={client.name || "Cliente"} loading="lazy" className="h-7 sm:h-8 w-auto object-contain opacity-40 grayscale" />
              ) : (
                <span key={i} className="text-xl sm:text-2xl font-semibold tracking-tight text-night/35 select-none">
                  {client.name}
                </span>
              )
            )}
          </div>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-10">
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 120}>
              <blockquote data-testid={`testimonial-${i}`} className="border-l-2 border-gold pl-6">
                <p className="text-base text-night/70 leading-relaxed">«{t.quote}»</p>
                <footer className="mt-4 text-xs font-mono tracking-[0.15em] text-night/50 uppercase">{t.author}</footer>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCTA = () => {
  const tx = useTexts();
  return (
    <section data-testid="final-cta-section" className="bg-night border-t border-line">
      <div className="px-6 sm:px-12 lg:px-20 py-24 lg:py-36 text-center">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-bone">
            {tx("home_cta_title", "Hai un prodotto o uno spazio da valorizzare?")}
          </h2>
          <p className="mt-4 text-base text-fog">{tx("home_cta_sub", "Parliamo del tuo progetto.")}</p>
          <Link
            to="/contact"
            data-testid="final-cta-button"
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone transition-colors duration-300"
          >
            {tx("home_cta_button", "CONTATTAMI")} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Home = () => {
  const [reelOpen, setReelOpen] = useState(false);
  const content = useSiteContent();

  return (
    <main data-testid="home-page">
      <Hero onPlayReel={() => setReelOpen(true)} image={content?.hero_image} />
      <Positioning onPlayReel={() => setReelOpen(true)} frames={content?.reel_frames} />
      <Stats />
      <Areas images={content ? [content.area_product, content.area_architecture, content.area_food, content.area_motion] : null} />
      <AISection image={content?.ai_image} />
      <SelectedWork />
      <Manifesto />
      <Clients />
      <FinalCTA />
      <ShowreelDialog open={reelOpen} onOpenChange={setReelOpen} video={content?.reel_video} poster={content?.reel_poster} />
    </main>
  );
};

export default Home;
